import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
    AuthService,
    ConditionType,
    DropdownMenuData,
    DropdownMenuItem,
    FEATURE_FLAGS,
    HeaderElement
} from '@ridango/core';
import Env from '@ridango/core/env';
import { FeatureFlagService } from '@ridango/feature-flags';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { defaultHeaderMenuItems, mobileHeaderMenuItems } from '../components/header/defaultHeaderMenuElements';
import { HelperService } from './helper.service';

@Injectable({
    providedIn: 'root'
})
export class HeaderMenuService implements OnDestroy {
    public menuItems: HeaderElement[] = [];

    private showBookARide = true;
    private isMobileView = window.innerWidth < 768 || this.env.IS_MOBILE_APP;
    private destroy$ = new Subject<void>();
    private openMenu = new Subject<void>();

    private readonly mobileMenuItems: HeaderElement[] =
        this.env.HEADER.MOBILE_MENU_ITEMS_ORDER || mobileHeaderMenuItems;
    public readonly openMenu$ = this.openMenu.asObservable();

    private _dropdownMenuItems$ = new BehaviorSubject<DropdownMenuItem[]>([]);
    private _useDropdownMenuSystem$ = new BehaviorSubject<boolean>(false);

    public readonly dropdownMenuItems$: Observable<DropdownMenuItem[]> = this._dropdownMenuItems$.asObservable();
    public readonly useDropdownMenuSystem$: Observable<boolean> = this._useDropdownMenuSystem$.asObservable();

    private _dropdownMenuData$ = new BehaviorSubject<{ [key: string]: DropdownMenuData }>({});
    public readonly dropdownMenuData$: Observable<{ [key: string]: DropdownMenuData }> =
        this._dropdownMenuData$.asObservable();

    constructor(
        @Inject('ENV') public env: Env,
        private helper: HelperService,
        private featureFlags: FeatureFlagService,
        private ridangoAuth: AuthService
    ) {
        this.subscriberForFeatureFlag();
        this.initializeDefaultMenuElements();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscriberForFeatureFlag(): void {
        this.featureFlags
            .getFlags(FEATURE_FLAGS.BookARide)
            .pipe(takeUntil(this.destroy$))
            .subscribe((bookARide) => {
                this.showBookARide = bookARide;
                this.initializeDefaultMenuElements();
            });
    }

    private initializeDefaultMenuElements(): void {
        this.menuItems = this.env.HEADER?.MENU_ITEMS_ORDER || defaultHeaderMenuItems;

        const dropdownItems = this.env.HEADER?.DROPDOWN_MENU?.ITEMS || [];
        const dropdownData = this.env.HEADER?.DROPDOWN_MENU?.DATA || {};
        const useDropdownSystem = this.env.HEADER?.DROPDOWN_MENU?.ENABLED || false;

        this._dropdownMenuItems$.next(dropdownItems);
        this._dropdownMenuData$.next(dropdownData);
        this._useDropdownMenuSystem$.next(useDropdownSystem);

        if (this.isMobileView) {
            this.handleMobileView();
        }
    }

    private handleMobileView(): void {
        const mobilePaths = this.mobileMenuItems.map((item) => item.path);
        this.menuItems = this.menuItems.filter((menuItem) => !mobilePaths.includes(menuItem.path));
        this.menuItems = this.env.HEADER?.MOBILE_MENU_ITEMS_ORDER || [...this.menuItems, ...this.mobileMenuItems];
    }

    private processMenuItems(items: HeaderElement[]): HeaderElement[] {
        const externalLinks = this.env.HEADER?.EXTERNAL_LINKS || {};

        return items.map((item) => {
            if (item.path === '/journey-planner' && externalLinks.map) {
                return {
                    ...item,
                    isExternal: true,
                    externalUrl: externalLinks.map
                };
            }

            if (item.path === '/explore' && externalLinks.explore) {
                return {
                    ...item,
                    isExternal: true,
                    externalUrl: externalLinks.explore
                };
            }

            return item;
        });
    }

    public getMenuItems(): HeaderElement[] {
        return this.processMenuItems(this.menuItems);
    }

    public handleOpenMenu() {
        this.openMenu.next();
    }

    public isCheckEnvironmentsConditions(conditions: ConditionType[]): boolean {
        const envValues: boolean[] = [];

        for (let i = 0; i < conditions.length; i++) {
            const envCondition = conditions[i];
            const isNegate = envCondition[0] === '!';

            const envConditionParts = envCondition.split('.');
            envConditionParts[0] = isNegate ? envConditionParts[0].slice(1) : envConditionParts[0];

            let envPartValue: Env | boolean = this.env;
            let envResultValue = false;

            for (const part of envConditionParts) {
                if (envPartValue !== undefined && envPartValue !== null) {
                    envPartValue = envPartValue[part];
                    if (isNegate && envPartValue === undefined) {
                        envResultValue = true;
                        break;
                    }

                    if (typeof envPartValue === 'boolean') {
                        envResultValue = envPartValue;
                        break;
                    }
                } else {
                    envResultValue = false;
                    break;
                }
            }

            envValues.push(isNegate ? !envResultValue : envResultValue);
        }

        return envValues.reduce((accum, item, index) => {
            if (index !== 0) {
                return accum && item;
            }
            return item;
        });
    }

    public isCheckBasicConditions(headerElement: HeaderElement): boolean {
        let isEnvCondition: boolean;
        let isSpecificCondition: boolean;

        if (this.env.ENABLE_PURCHASE_V2 && headerElement.path === '/purchase/ticket') {
            headerElement.path = '/purchase-v2';
        }

        if (this.env.HEADER?.HIDE_BIKE_GARAGE && headerElement.path === '/purchase/garage') {
            return false;
        }

        if (headerElement.condition && headerElement.condition.length > 0) {
            const envConditions = headerElement.condition.filter((item) => {
                return !Object.values(ConditionType).includes(item);
            });

            isEnvCondition = envConditions.length ? this.isCheckEnvironmentsConditions(envConditions) : true;
            isSpecificCondition = this.isCheckSpecificConditions(headerElement);
        } else {
            isEnvCondition = true;
            isSpecificCondition = true;
        }

        const isAllowedPath = headerElement.flags?.includes('isNotCheckAllowedPath')
            ? true
            : this.helper.isAllowedPath(headerElement.path);

        return isAllowedPath && isSpecificCondition && isEnvCondition;
    }

    public isCheckSpecificConditions(headerElement: HeaderElement): boolean {
        if (headerElement.condition?.includes(ConditionType.SHOW_BOOK_ARIDE)) {
            return this.showBookARide;
        }

        if (headerElement.condition?.includes(ConditionType.AUTH_REQUIRED)) {
            return this.ridangoAuth.getLoginStatus();
        }

        return true;
    }

    public isDropdownMenuItemAllowed(menuItem: DropdownMenuItem): boolean {
        if (menuItem.condition && menuItem.condition.length > 0) {
            const envConditions = menuItem.condition.filter((item) => {
                return !Object.values(ConditionType).includes(item);
            });

            const isEnvCondition = envConditions.length ? this.isCheckEnvironmentsConditions(envConditions) : true;
            const isSpecificCondition = this.isCheckSpecificConditionsForDropdown(menuItem);

            return isEnvCondition && isSpecificCondition;
        }
        return true;
    }

    private isCheckSpecificConditionsForDropdown(menuItem: DropdownMenuItem): boolean {
        if (menuItem.condition?.includes(ConditionType.ALWAYS)) {
            return true;
        }

        if (menuItem.condition?.includes(ConditionType.SHOW_BOOK_ARIDE)) {
            return this.showBookARide;
        }

        if (menuItem.condition?.includes(ConditionType.AUTH_REQUIRED)) {
            const loginStatus = this.ridangoAuth.getLoginStatus();
            return loginStatus;
        }

        return true;
    }

    public getDropdownMenuData(): Observable<{ [key: string]: DropdownMenuData }> {
        return this.dropdownMenuData$;
    }
}
